import type {
  AdminIamAssignmentListItem,
  AdminIamAssignmentsParams,
  AdminUsersListParams,
} from '../api/adminConsole';

// Query builders for the RBAC Phase 2 admin-api list endpoints (pure; unit-tested).

export const ADMIN_USERS_MAX_LIMIT = 100;
export const ADMIN_AUDIT_MAX_LIMIT = 200;
export const ADMIN_AUDIT_DEFAULT_LIMIT = 50;

/** Data-grid column field → `GET /admin/users` `sort` field. Unlisted columns are not sortable. */
export const USERS_SORT_FIELDS: Readonly<Record<string, string>> = {
  email: 'email',
  displayName: 'displayName',
  lastLoginAt: 'lastLoginAt',
  createdAt: 'createdAt',
};

export type UsersStatusFilter = '' | 'active' | 'inactive' | 'never_signed_in';

export type UsersGridState = {
  page: number;
  pageSize: number;
  sort?: ReadonlyArray<{ field: string; sort?: 'asc' | 'desc' | null }>;
  q?: string;
  status?: UsersStatusFilter;
  roleId?: string;
};

/** Grid paging/sort + filter controls → `GET /admin/users` query (blank filters omitted). */
export function buildUsersListParams(state: UsersGridState): AdminUsersListParams {
  const limit = Math.max(1, Math.min(ADMIN_USERS_MAX_LIMIT, Math.floor(state.pageSize) || 50));
  const page = Math.max(0, Math.floor(state.page) || 0);
  const params: AdminUsersListParams = { limit, offset: page * limit };
  const q = state.q?.trim();
  if (q) params.q = q;
  if (state.status) params.status = state.status;
  if (state.roleId) params.roleId = state.roleId;
  const first = state.sort?.find((s) => s.sort && USERS_SORT_FIELDS[s.field]);
  if (first?.sort) params.sort = `${USERS_SORT_FIELDS[first.field]}:${first.sort}`;
  return params;
}

export type AuditFilters = {
  actorEmail?: string;
  actions?: readonly string[];
  entityType?: string;
  entityId?: string;
  /** Rows about a staff user, including their role assignments (keyed by assignment id). */
  subjectUserId?: string;
  /** ISO or `YYYY-MM-DD`; a date-only `to` covers the whole day. */
  from?: string;
  to?: string;
};

function dateBoundToIso(value: string | undefined, endOfDay: boolean): string | undefined {
  const raw = value?.trim();
  if (!raw) return undefined;
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    const [y, m, d] = raw.split('-').map(Number);
    const local = endOfDay ? new Date(y, m - 1, d, 23, 59, 59, 999) : new Date(y, m - 1, d, 0, 0, 0, 0);
    return local.toISOString();
  }
  const ms = Date.parse(raw);
  return Number.isNaN(ms) ? undefined : new Date(ms).toISOString();
}

/** Audit filters + cursor → `GET /admin/audit-logs` query. `action` is comma-separated. */
export function buildAuditQuery(
  filters: AuditFilters,
  cursor?: string | null,
  limit: number = ADMIN_AUDIT_DEFAULT_LIMIT,
): Record<string, string | number> {
  const out: Record<string, string | number> = {
    limit: Math.max(1, Math.min(ADMIN_AUDIT_MAX_LIMIT, Math.floor(limit) || ADMIN_AUDIT_DEFAULT_LIMIT)),
  };
  const actor = filters.actorEmail?.trim();
  if (actor) out.actorEmail = actor;
  const actions = [...new Set((filters.actions ?? []).map((a) => a.trim()).filter(Boolean))];
  if (actions.length > 0) out.action = actions.join(',');
  const entityType = filters.entityType?.trim();
  if (entityType) out.entityType = entityType;
  const entityId = filters.entityId?.trim();
  if (entityId) out.entityId = entityId;
  const subjectUserId = filters.subjectUserId?.trim();
  if (subjectUserId) out.subjectUserId = subjectUserId;
  const from = dateBoundToIso(filters.from, false);
  if (from) out.from = from;
  const to = dateBoundToIso(filters.to, true);
  if (to) out.to = to;
  if (cursor) out.cursor = cursor;
  return out;
}

/** Deep link into the admin audit page for one entity (entity type optional). */
export function auditDeepLink(entityId: string, entityType?: string): string {
  const qs = new URLSearchParams();
  if (entityType) qs.set('entityType', entityType);
  qs.set('entityId', entityId);
  return `/admin/audit-log?${qs.toString()}`;
}

/** Deep link into the admin audit page for everything about one staff user (incl. their roles). */
export function auditUserDeepLink(userId: string): string {
  const qs = new URLSearchParams();
  qs.set('subjectUserId', userId);
  return `/admin/audit-log?${qs.toString()}`;
}

/** Audit filters → the page URL (inverse of `auditFiltersFromSearch`; blank filters omitted). */
export function auditFiltersToSearch(f: AuditFilters): URLSearchParams {
  const p = new URLSearchParams();
  if (f.actorEmail?.trim()) p.set('actorEmail', f.actorEmail.trim());
  for (const a of f.actions ?? []) p.append('action', a);
  if (f.entityType?.trim()) p.set('entityType', f.entityType.trim());
  if (f.entityId?.trim()) p.set('entityId', f.entityId.trim());
  if (f.subjectUserId?.trim()) p.set('subjectUserId', f.subjectUserId.trim());
  if (f.from) p.set('from', f.from);
  if (f.to) p.set('to', f.to);
  return p;
}

/** Audit filters from the page URL (the inverse of the deep links plus the other filters). */
export function auditFiltersFromSearch(search: string): AuditFilters {
  const p = new URLSearchParams(search);
  const actions = p.getAll('action').flatMap((a) => a.split(',')).map((a) => a.trim()).filter(Boolean);
  return {
    actorEmail: p.get('actorEmail') ?? '',
    actions,
    entityType: p.get('entityType') ?? '',
    entityId: p.get('entityId') ?? '',
    subjectUserId: p.get('subjectUserId') ?? '',
    from: p.get('from') ?? '',
    to: p.get('to') ?? '',
  };
}

/** Filename from a `Content-Disposition` header (RFC 5987 `filename*` preferred). */
export function filenameFromContentDisposition(header: string | null | undefined): string | null {
  if (!header) return null;
  const star = /filename\*\s*=\s*(?:UTF-8|utf-8)?''([^;]+)/.exec(header);
  if (star) {
    try {
      const name = decodeURIComponent(star[1].trim().replace(/^"|"$/g, ''));
      if (name) return sanitizeFilename(name);
    } catch {
      /* fall through */
    }
  }
  const plain = /filename\s*=\s*("([^"]*)"|[^;]+)/.exec(header);
  const name = (plain?.[2] ?? plain?.[1] ?? '').trim();
  return name ? sanitizeFilename(name) : null;
}

function sanitizeFilename(name: string): string {
  return name.replace(/[\\/:*?"<>|]+/g, '_');
}

// ── Expiring access tabs ──────────────────────────────────────────────────────────────────

export type ExpiringTab = 'next7' | 'next30' | 'expired30';

const DAY_MS = 24 * 60 * 60 * 1000;

/** Page size for the expiring-access page walk, and the most rows one tab will load. */
export const EXPIRING_PAGE_SIZE = 100;
export const EXPIRING_MAX_ROWS = 1000;

/**
 * The `GET /admin/iam/assignments` queries behind one tab (no `limit`/`offset`: the caller pages
 * through them with `fetchAllAssignmentPages`), plus a client-side guard for older servers that
 * ignore `expiresAfter`. Every query is bounded on both sides and sorted `expiresAt` ascending.
 * The server's `active` status excludes the 7-day `expiring` window, so "next 30 days" asks for
 * both statuses.
 * - next 7 days:  `expiring`, now < expiresAt < now+7d.
 * - next 30 days: `expiring` + `active`, now < expiresAt < now+30d.
 * - expired in the last 30 days: `expired`, now-30d < expiresAt < now.
 */
export function expiringTabQueries(
  tab: ExpiringTab,
  now: number = Date.now(),
): { queries: AdminIamAssignmentsParams[]; keep: (item: AdminIamAssignmentListItem) => boolean } {
  const iso = (ms: number) => new Date(ms).toISOString();
  const within = (lo: number, hi: number) => (item: AdminIamAssignmentListItem) => {
    if (!item.expiresAt) return false;
    const t = Date.parse(item.expiresAt);
    return t >= lo && t <= hi;
  };
  if (tab === 'next7' || tab === 'next30') {
    const end = now + (tab === 'next7' ? 7 : 30) * DAY_MS;
    const bounds = { expiresAfter: iso(now), expiresBefore: iso(end), sort: 'expiresAt' as const };
    const queries: AdminIamAssignmentsParams[] = [{ status: 'expiring', ...bounds }];
    if (tab === 'next30') queries.push({ status: 'active', ...bounds });
    return { queries, keep: within(now, end) };
  }
  const since = now - 30 * DAY_MS;
  return {
    queries: [{ status: 'expired', expiresAfter: iso(since), expiresBefore: iso(now), sort: 'expiresAt' }],
    keep: within(since, now),
  };
}

/**
 * Walks `limit`/`offset` pages of one assignments query until `total` is reached, a short page
 * arrives, or `maxRows` rows are loaded (`truncated` then says more exist).
 */
export async function fetchAllAssignmentPages(
  fetchPage: (params: AdminIamAssignmentsParams) => Promise<{ total: number; items: AdminIamAssignmentListItem[] }>,
  params: AdminIamAssignmentsParams,
  pageSize: number = EXPIRING_PAGE_SIZE,
  maxRows: number = EXPIRING_MAX_ROWS,
): Promise<{ items: AdminIamAssignmentListItem[]; total: number; truncated: boolean }> {
  const limit = Math.max(1, Math.min(100, Math.floor(pageSize) || EXPIRING_PAGE_SIZE));
  const items: AdminIamAssignmentListItem[] = [];
  let total = 0;
  for (let offset = 0; offset < maxRows; offset += limit) {
    const page = await fetchPage({ ...params, limit: Math.min(limit, maxRows - offset), offset });
    total = page.total;
    items.push(...page.items);
    if (page.items.length === 0 || items.length >= total) break;
  }
  return { items, total, truncated: total > items.length };
}

/** Merge tab results (dedupe by id), soonest expiry first; expired tab most recent first. */
export function mergeExpiringResults(
  tab: ExpiringTab,
  pages: ReadonlyArray<ReadonlyArray<AdminIamAssignmentListItem>>,
  keep: (item: AdminIamAssignmentListItem) => boolean,
): AdminIamAssignmentListItem[] {
  const byId = new Map<string, AdminIamAssignmentListItem>();
  for (const page of pages) for (const item of page) if (keep(item)) byId.set(item.id, item);
  const ms = (i: AdminIamAssignmentListItem) => (i.expiresAt ? Date.parse(i.expiresAt) : Number.MAX_SAFE_INTEGER);
  const items = [...byId.values()];
  return tab === 'expired30' ? items.sort((a, b) => ms(b) - ms(a)) : items.sort((a, b) => ms(a) - ms(b));
}
